import { autoLayout } from "@docit/flow-editor";
import { Background, Controls, type Edge, type Node, ReactFlow } from "@xyflow/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  ReasoningMemoryGraph as Graph,
  ReasoningSession,
  ReasoningTrace,
} from "~/api/reasoningTypes";
import "@xyflow/react/dist/style.css";

const color = (kind: string) => {
  if (kind === "session") return "#725ce8";
  if (kind === "candidate") return "#268a75";
  if (kind === "evaluation" || kind === "acceptance") return "#2863ae";
  if (kind === "event") return "#8883a5";
  if (kind === "accepted-reasoning" || kind === "memory") return "#b06d2e";
  if (kind === "canopy") return "#4886a0";
  return "#69728a";
};
const edgeKey = (edge: Graph["edges"][number]) =>
  `${edge.source}|${edge.target}|${edge.kind}|${edge.revision ?? ""}|${edge.digest ?? ""}`;

/** Visualizes Neo4j identities and links; Postgres remains the record of truth. */
export function ReasoningMemoryGraph({
  graph,
  session,
  trace,
}: {
  graph: Graph;
  session: ReasoningSession;
  trace: ReasoningTrace | null;
}) {
  const { t } = useTranslation();
  const [showEvents, setShowEvents] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
  const [positions, setPositions] = useState<Record<string, { x: number; y: number }>>({});
  const [recent, setRecent] = useState<string[]>([]);
  const [recentEdges, setRecentEdges] = useState<string[]>([]);
  const [updatedAt, setUpdatedAt] = useState("");
  const previous = useRef<{
    sessionId: string;
    graph: Graph | null;
    nodes: Map<string, string>;
    edges: Set<string>;
  }>({
    sessionId: session.id,
    graph: null,
    nodes: new Map(),
    edges: new Set(),
  });
  useEffect(() => {
    if (previous.current.sessionId !== session.id)
      previous.current = { sessionId: session.id, graph: null, nodes: new Map(), edges: new Set() };
    if (previous.current.graph === graph) return;
    const nextNodes = new Map(
      graph.nodes.map((node) => [
        node.id,
        `${node.kind}|${node.label}|${node.revision ?? ""}|${node.digest ?? ""}`,
      ]),
    );
    const nextEdges = new Set(graph.edges.map(edgeKey));
    setRecent(
      [...nextNodes]
        .filter(([id, version]) => previous.current.nodes.get(id) !== version)
        .map(([id]) => id),
    );
    setRecentEdges([...nextEdges].filter((id) => !previous.current.edges.has(id)));
    previous.current.nodes = nextNodes;
    previous.current.edges = nextEdges;
    previous.current.graph = graph;
    setUpdatedAt(new Date().toLocaleTimeString());
  }, [graph, session.id]);
  const view = useMemo(() => {
    const visible = graph.nodes.filter((node) => showEvents || node.kind !== "event");
    const ids = new Set(visible.map((node) => node.id));
    const links = graph.edges.filter((edge) => ids.has(edge.source) && ids.has(edge.target));
    const edges: Edge[] = links.map((edge) => ({
      id: edgeKey(edge),
      source: edge.source,
      target: edge.target,
      type: "smoothstep",
      label: edge.kind.replaceAll("_", " ").toLowerCase(),
      style: {
        stroke:
          edge.kind === "CONTRADICTS"
            ? "#bd4444"
            : edge.kind === "SUPPORTS"
              ? "#268a75"
              : "#8992a3",
        strokeWidth: recentEdges.includes(edgeKey(edge)) ? 3 : 1.5,
        strokeDasharray: edge.kind === "REPORTED_CAUSE" ? "5 4" : undefined,
      },
      labelStyle: { fill: "var(--text)" },
      labelBgStyle: { fill: "var(--bg)" },
    }));
    const nodes: Node[] = visible.map((node) => ({
      id: node.id,
      position: { x: 0, y: 0 },
      data: {
        label: (
          <span>
            <strong>{node.kind}</strong>
            <br />
            {node.label}
          </span>
        ),
      },
      ariaLabel: `${node.kind}: ${node.label}`,
      // Logical start, so the label reads correctly in the Hebrew face; the canvas itself stays LTR.
      style: {
        width: 190,
        border: `2px solid ${color(node.kind)}`,
        boxShadow: recent.includes(node.id) ? `0 0 0 4px ${color(node.kind)}55` : undefined,
        background: "var(--bg)",
        color: "var(--text)",
        borderRadius: 9,
        textAlign: "start",
      },
    }));
    return {
      nodes: autoLayout(nodes, edges, "LR").map((node) => ({
        ...node,
        position: positions[node.id] ?? node.position,
      })),
      edges,
    };
  }, [graph, showEvents, recent, recentEdges, positions]);
  const selected = graph.nodes.find((node) => node.id === selectedId);
  const selectedEdge = graph.edges.find((edge) => edgeKey(edge) === selectedEdgeId);
  const record = selectedId?.startsWith("record:")
    ? session.records.find((item) => item.id === selectedId.slice(7))
    : null;
  const event = selectedId?.startsWith("event:")
    ? trace?.trace.events.find((item) => item.id === selectedId.slice(6))
    : null;
  return (
    <section
      aria-label={t("Neo4j reasoning memory graph")}
      className="flex flex-col gap-3 rounded border border-divider p-3"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h4 className="font-semibold">{t("Memory and provenance graph")}</h4>
          <p className="text-small text-text-secondary">
            {t("Neo4j projection · {{status}} · records {{projected}}/{{expected}}", {
              status: graph.status,
              projected: graph.nodes.filter((n) => n.id.startsWith("record:")).length,
              expected: graph.expectedRecords,
            })}
            {updatedAt && ` · ${t("checked {{time}}", { time: updatedAt })}`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label>
            <input
              type="checkbox"
              checked={showEvents}
              onChange={(e) => setShowEvents(e.target.checked)}
            />{" "}
            {t("Show chain events")}
          </label>
          <button type="button" onClick={() => setPositions({})}>
            {t("Arrange")}
          </button>
        </div>
      </div>
      {graph.status === "lagging" && (
        <p role="status">
          {t("The memory projection is catching up with Postgres. New records may appear shortly.")}
        </p>
      )}
      {graph.status === "unavailable" && (
        <p role="status">
          {t("Neo4j is unavailable. The recorded chain below remains available from Postgres.")}
        </p>
      )}
      {(recent.length > 0 || recentEdges.length > 0) && (
        <p role="status">
          {t(
            "Latest update: {{nodes}} new or changed nodes, {{links}} new or changed links. Highlighted in the graph.",
            { nodes: recent.length, links: recentEdges.length },
          )}
        </p>
      )}
      <div className="h-[480px] rounded border border-divider" dir="ltr">
        <ReactFlow
          nodes={view.nodes}
          edges={view.edges}
          onNodesChange={(changes) =>
            setPositions((current) => {
              const next = { ...current };
              for (const change of changes)
                if (change.type === "position" && change.position)
                  next[change.id] = change.position;
              return next;
            })
          }
          onNodeClick={(_, node) => {
            setSelectedId(node.id);
            setSelectedEdgeId(null);
          }}
          onEdgeClick={(_, edge) => {
            setSelectedEdgeId(edge.id);
            setSelectedId(null);
          }}
          nodesConnectable={false}
          nodesDraggable
          fitView
          minZoom={0.15}
          maxZoom={2}
        >
          <Background />
          <Controls showInteractive={false} />
        </ReactFlow>
      </div>
      {selected && (
        <aside
          aria-label={t("Selected reasoning graph node")}
          className="rounded border border-divider p-3"
        >
          <strong>
            {selected.kind}: {selected.label}
          </strong>
          <p className="text-small text-text-secondary">
            {t("Revision {{revision}} · digest {{digest}}", {
              revision: selected.revision ?? t("unknown"),
              digest: selected.digest ?? t("not projected"),
            })}
          </p>
          {record && (
            <>
              <p>
                {record.eligible
                  ? t("Eligible")
                  : t("Needs review: {{reason}}", {
                      reason: record.ineligibilityReason ?? t("unknown reason"),
                    })}
              </p>
              {typeof record.payload.summary === "string" && <p>{record.payload.summary}</p>}
              {typeof record.payload.rationale === "string" && (
                <p className="whitespace-pre-wrap">
                  {t("Recorded rationale: {{rationale}}", { rationale: record.payload.rationale })}
                </p>
              )}
              {typeof record.payload.content === "string" && (
                <details>
                  <summary>{t("Retained candidate content")}</summary>
                  <p className="whitespace-pre-wrap">{record.payload.content}</p>
                </details>
              )}
            </>
          )}
          {event && <p>{String(event.payload.summary ?? event.payload.status ?? event.kind)}</p>}
          <p className="text-small text-text-secondary">
            {t("This view shows retained records and declared links, not hidden model thoughts.")}
          </p>
        </aside>
      )}
      {selectedEdge && (
        <aside
          aria-label={t("Selected reasoning graph link")}
          className="rounded border border-divider p-3"
        >
          <strong>{selectedEdge.kind.replaceAll("_", " ")}</strong>
          <p>
            {selectedEdge.source} → {selectedEdge.target}
          </p>
          <p className="text-small text-text-secondary">
            {t("Source revision {{revision}} · source digest {{digest}}", {
              revision: selectedEdge.revision ?? t("not projected"),
              digest: selectedEdge.digest ?? t("not projected"),
            })}
          </p>
          <p className="text-small text-text-secondary">
            {t(
              "Evidence and cause links are recorded claims; inspect the source and evaluation before relying on them.",
            )}
          </p>
        </aside>
      )}
    </section>
  );
}
